import { Link } from "react-router-dom";
import { Zap, Target, TrendingUp } from "lucide-react";
import { Logo } from "@/components/Logo";

export function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">

      {/* ── Left coral brand panel ── */}
      <div
        className="relative hidden flex-col justify-between overflow-hidden p-12 lg:flex"
        style={{ background: "linear-gradient(145deg, #C94422 0%, #E8522A 45%, #F2744A 100%)" }}
      >
        {/* Decorative orb */}
        <div
          className="pointer-events-none absolute right-[-80px] top-[-80px] h-80 w-80 rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #FFD4A3 0%, transparent 70%)" }}
        />

        {/* Logo */}
        <div className="relative z-10">
          <Link to="/" className="inline-block transition-opacity hover:opacity-90">
            <Logo variant="white" size="lg" />
          </Link>
        </div>

        {/* Headline */}
        <div className="relative z-10">
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white md:text-5xl">
            Know what to create
            <br />
            <span className="text-white/70">before you create it.</span>
          </h2>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/75">
            Your Creator DNA, niche intelligence, research and performance feedback —
            all working together in one connected workflow.
          </p>

          {/* Feature pills */}
          <div className="mt-8 flex flex-wrap gap-2">
            {[
              { icon: Zap,       label: "Market Intelligence" },
              { icon: Target,    label: "Opportunity Scoring" },
              { icon: TrendingUp,label: "Performance Analytics" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm"
              >
                <Icon className="h-3 w-3" />
                {label}
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-xs text-white/40">Content Intelligence OS</p>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex flex-col bg-surface">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between p-5 lg:hidden">
          <Link to="/">
            <div className="flex items-center gap-2.5">
              <span
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl shadow-sm"
                style={{ background: "linear-gradient(135deg, #E8522A, #F2744A)" }}
              >
                <span className="font-display text-base font-extrabold text-white">V</span>
              </span>
              <span className="font-display text-lg font-bold text-foreground">Virality</span>
            </div>
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 pb-12 pt-8 lg:pt-0">
          <div className="w-full max-w-sm">
            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{title}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
            <div className="mt-8">{children}</div>
            <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, children, error }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-semibold text-foreground">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
